import { useState } from 'react'
import React from 'react'
import { ArrowForward } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import StepperPopup from '../../../../components/StepperPopup'
import PaymentDetails from './components/common/PaymentDetails'
import ConsumerDeliveryDetails from './components/consumer/ConsumerDeliveryDetails'
import ProducerPlantDetails from './components/producer/ProducerPlantDetails'
import ConsumerOtherDataDetails from './components/consumer/ConsumerOtherDataDetails'
import ProducerOtherDataDetails from './components/producer/ProducerOtherDataDetails'
import BillingAddressDetails from './components/common/BillingAddressDetails'
import { useSelector } from 'react-redux'
import EditUserDetails from './components/common/EditUserDetails'
import ConsumerConfirmUserDetails from './components/consumer/ConsumerConfirmUserDetails'
import ProducerConfirmUserDetails from './components/producer/ProducerConfirmUserDetails'

/**
 * The main component that shows the user details after user clicks on the details in the table of Nutzerverwaltung page.
 * This also holds the button to edit the details of a contract.
 *
 * @returns {React.ReactNode} - Returns all the component that are show in user details page.
 */

export default function EditContractFormPopup({
  onLoadingChanged,
  onFormCompleted,
  onIsError,
  contract,
  onIsFormOpened,
  isEditingContract,
  refetch,
}) {
  const { t } = useTranslation()
  const [step, setStep] = useState(0)
  const [canNext, setCanNext] = useState(false)
  const [submit, setSubmit] = useState(0)
  const [confirmed, setConfirmed] = useState(0)
  const [canNextOne, setCanNextOne] = useState(false)
  const [canNextTwo, setCanNextTwo] = useState(false)
  const [canNextThree, setCanNextThree] = useState(false)
  const [canNextBilling, setCanNextBilling] = useState(false)
  const [submitOne, setSubmitOne] = useState(0)
  const [submitTwo, setSubmitTwo] = useState(0)
  const [submitThree, setSubmitThree] = useState(0)
  const [submitBilling, setSubmitBilling] = useState(0)
  const handleNext = () => setStep(step + 1)
  const handlePrev = () => setStep(step - 1)
  const [payload, setPayload] = useState()
  const [payloadOne, setPayloadOne] = useState()
  const [payloadTwo, setPayloadTwo] = useState()
  const [payloadThree, setPayloadThree] = useState()
  const [payloadBilling, setPayloadBilling] = useState()

  const isBillingAddressSame = useSelector(
    (state) => state.address.isBillingAddressSame
  )

  const handleSetPayload = (input) => {
    setPayload(input)
  }
  const handleSetPayloadOne = (input) => {
    setPayloadOne(input)
  }
  const handleSetPayloadTwo = (input) => {
    setPayloadTwo(input)
  }

  const handleSetPayloadThree = (input) => {
    setPayloadThree(input)
  }

  const handleSetPayloadBilling = (input) => {
    setPayloadBilling(input)
  }

  const setSubmittingConfirm = () => {
    onIsFormOpened(false)
  }
  const handleNextevent = (input) => {
    handleNext()
  }
  const onClose = (_event, reason) => {
    if (reason !== 'backdropClick') {
      setStep(0)
      setPayload(null)
    }
  }
  /** @type {import("@mui/material").DialogProps['onClose']} */
  const onCloseOne = (_event, reason) => {
    if (reason !== 'backdropClick') {
      setPayloadOne(null)

      setStep(1)
    }
  }
  /** @type {import("@mui/material").DialogProps['onClose']} */
  const onCloseTwo = (_event, reason) => {
    if (reason !== 'backdropClick') {
      setPayloadTwo(null)
      setStep(2)
    }
  }
  /** @type {import("@mui/material").DialogProps['onClose']} */
  const onCloseThree = (_event, reason) => {
    if (reason !== 'backdropClick') {
      setPayloadThree(null)
      setStep(3)
    }
  }
  const isConsumer = contract?.energyRole === 'CONSUMER'

  // Transform the payload to match the new API structure
  const confirmPayload = {
    // User information
    salutation: payload?.salutation,
    firstName:
      payload?.salutation !== 'COMPANY' ? payload?.firstName : undefined,
    lastName: payload?.salutation !== 'COMPANY' ? payload?.lastName : undefined,
    companyName:
      payload?.salutation === 'COMPANY' ? payload?.firstName : undefined,
    email: payload?.email,
    phone: payload?.phone,
    dateOfBirth: payload?.dateOfBirth,

    // Address information
    address: payload?.address,
    billingAddress: !isBillingAddressSame
      ? {
          name: payloadBilling?.billingAddress?.name,
          city: payloadBilling?.billingAddress?.city,
          street: payloadBilling?.billingAddress?.street,
          houseNumber: payloadBilling?.billingAddress?.houseNumber,
          postalCode: payloadBilling?.billingAddress?.postalCode,
        }
      : undefined,

    // Company information (for companies)
    registeredCourt: payload?.registeredCourt,
    registrationNumber: payload?.registrationNumber,

    // Contract information
    contractNumber: payloadThree?.contractNumber,
    contractStatus: payloadThree?.contractStatus,
    contractType: payloadThree?.contractType,

    // Energy information
    energyPreference: payloadTwo?.energyPreference,
    energyPlantType: payloadTwo?.plantDetails?.energyPlantType,
    startOfDeliveryDate:
      payloadTwo?.startOfDeliveryDate ?? payloadTwo?.startOfDelivery,
    isStartOfDeliveryNextPossibleDate:
      payloadTwo?.isStartOfDeliveryNextPossibleDate,

    // Plant information (flat structure instead of nested)
    plantName: payloadTwo?.plantDetails?.plantName,
    plantAddress: payloadTwo?.plantAddress,
    installedPowerKw: payloadTwo?.plantDetails?.installedPowerKw,
    commissioningDate: payloadTwo?.plantDetails?.commissioningDate,
    marketLocation: payloadTwo?.plantDetails?.marketLocation,
    meterNumber: payloadTwo?.plantDetails?.meterNumber,
    gridOperator: payloadTwo?.plantDetails?.gridOperator,
    plantKey: payloadTwo?.plantDetails?.plantKey,

    // Payment information (updated field names)
    paymentDetails: {
      creditInstituteName: payloadOne?.creditInstituteName,
      bic: payloadOne?.bic,
      iban: payloadOne?.iban,
      accountHolderName: payloadOne?.accountHolderName,
      sepaAccepted: payloadOne?.sepaAccepted,
      paymentMethod: 'SEPA',
    },

    // Boolean fields (updated names)
    ...(!isConsumer && {
      isAlreadyClientOfUtility: payloadTwo?.isAlreadyClientOfUtility,
      utilityContractNumber: payloadTwo?.utilityContractNumber,
    }),
    isMarketingInformationConsentGiven:
      payloadThree?.isMarketingInformationConsentGiven,
    isTermsAndConditionsAccepted: payloadThree?.isTermsAndConditionsAccepted,
    ...(!isConsumer && {
      isSalesTaxLiable: payloadThree?.isSalesTaxLiable,
    }),
    isPlantTermsAndConditionsAccepted:
      payloadTwo?.plantDetails?.isPlantTermsAndConditionsAccepted,
    isPlantAddressSameAsAddress: payloadTwo?.isPlantAddressSameAsAddress,
    isBillingAddressSameAsAddress: isBillingAddressSame,

    // Additional fields
    currentUtilitySupplierName: payloadTwo?.currentUtilitySupplierName,
    endCustomerNumber: payloadTwo?.endCustomerNumber,

    // Contract identifier
    uuid: contract?.uuid,
  }

  return (
    <StepperPopup
      title={t('LBLEditUser')}
      isOpen={isEditingContract}
      setIsOpen={onIsFormOpened}
      skipBillingAddress={isBillingAddressSame}
      steps={[
        {
          label: t('LBLUserInfo'),
          component: (
            <EditUserDetails
              setPayload={handleSetPayload}
              handleClose={onClose}
              confirmPayload={handleNext}
              payload={contract}
              setSubmit={setSubmit}
              setCanNext={setCanNext}
              submit={submit}
            />
          ),
          nextAction: {
            label: t('LBLFurther'),
            icon: <ArrowForward />,
            action: () => {
              setSubmit((prevState) => prevState + 1)
            },
            canNext: canNext,
          },
        },
        {
          label: t('LBLBillingAddressInfo'),
          component: (
            <BillingAddressDetails
              setPayload={handleSetPayloadBilling}
              handleClose={onClose}
              confirmPayload={handleNext}
              payload={contract}
              setSubmit={setSubmitBilling}
              setCanNext={setCanNextBilling}
              submit={submitBilling}
            />
          ),
          nextAction: {
            label: t('LBLFurther'),
            icon: <ArrowForward />,
            action: () => {
              setSubmitBilling((prevState) => prevState + 1)
            },
            canNext: canNextBilling,
          },
        },
        {
          label: t('LBLAccountData'),
          component: (
            <PaymentDetails
              setPayloadOne={handleSetPayloadOne}
              handleClose={onCloseOne}
              confirmPayload={handleNextevent}
              payload={contract}
              setSubmit={setSubmitOne}
              setCanNext={setCanNextOne}
              submit={submitOne}
            />
          ),
          nextAction: {
            label: t('LBLFurther'),
            icon: <ArrowForward />,
            action: () => {
              setSubmitOne((prevState) => prevState + 1)
            },
            canNext: canNextOne,
          },
        },
        {
          label: isConsumer
            ? t('LBLDeliveryPoint')
            : t('LBLGenerationPlantDetails'),
          component: isConsumer ? (
            <ConsumerDeliveryDetails
              setPayload={handleSetPayloadTwo}
              handleClose={onCloseTwo}
              confirmPayload={handleNext}
              payload={contract}
              setSubmit={setSubmitTwo}
              setCanNext={setCanNextTwo}
              submit={submitTwo}
            />
          ) : (
            <ProducerPlantDetails
              setPayload={handleSetPayloadTwo}
              handleClose={onCloseTwo}
              confirmPayload={handleNext}
              payload={contract}
              setSubmit={setSubmitTwo}
              setCanNext={setCanNextTwo}
              submit={submitTwo}
            />
          ),
          nextAction: {
            label: t('LBLFurther'),
            icon: <ArrowForward />,
            action: () => {
              setSubmitTwo((prevState) => prevState + 1)
            },
            canNext: canNextTwo,
          },
        },
        {
          label: t('LBLOtherData'),
          component: isConsumer ? (
            <ConsumerOtherDataDetails
              setPayloadThree={handleSetPayloadThree}
              handleClose={onCloseThree}
              confirmPayload={handleNext}
              payload={contract}
              setSubmit={setSubmitThree}
              setCanNext={setCanNextThree}
              submit={submitThree}
            />
          ) : (
            <ProducerOtherDataDetails
              setPayloadThree={handleSetPayloadThree}
              handleClose={onCloseThree}
              confirmPayload={handleNext}
              payload={contract}
              setSubmit={setSubmitThree}
              setCanNext={setCanNextThree}
              submit={submitThree}
            />
          ),
          nextAction: {
            label: t('LBLFurther'),
            icon: <ArrowForward />,
            action: () => {
              setSubmitThree((prevState) => prevState + 1)
            },
            canNext: canNextThree,
          },
        },
        {
          label: t('confirmInfo'),
          component: isConsumer ? (
            <ConsumerConfirmUserDetails
              payload={confirmPayload}
              handleClose={onClose}
              openFeedback={() => onFormCompleted(true)}
              backToForm={handlePrev}
              setError={() => onIsError(true)}
              setSubmittingConfirm={setSubmittingConfirm}
              submit={confirmed}
              setSubmit={setConfirmed}
              refetch={refetch}
            />
          ) : (
            <ProducerConfirmUserDetails
              payload={confirmPayload}
              handleClose={onClose}
              openFeedback={() => onFormCompleted(true)}
              backToForm={handlePrev}
              setError={() => onIsError(true)}
              setSubmittingConfirm={setSubmittingConfirm}
              submit={confirmed}
              setSubmit={setConfirmed}
              refetch={refetch}
            />
          ),
          nextAction: {
            label: t('LBLSave'),
            action: () => {
              setConfirmed((prevState) => prevState + 1)
              onIsFormOpened(false)
              onLoadingChanged(false)
            },
          },
        },
      ]}
    />
  )
}
